import type { StorefrontBlogPost, StorefrontCategory, StorefrontProduct } from './dropify'
import { toExternalImage } from './dropify'
import { getProductOrderFacts, isMaskProduct, maskMetaDescription } from './productOrdering'

export const SITE_URL = 'https://dropify.id'
export const SITE_NAME = 'Dropify'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`
export const INDEXABLE_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

export function absoluteUrl(path = '/') {
  return new URL(path, SITE_URL).toString()
}

export function stripHtml(value: string | null | undefined) {
  if (!value) return ''
  return value
    .replace(/<\s*br\s*\/?>/gi, ' ')
    .replace(/<\/\s*(p|div|li|h[1-6]|tr|blockquote)\s*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(#(?:x[0-9a-f]+|\d+)|amp|lt|gt|quot|apos|nbsp);/gi, (entity, token: string) => {
      const normalized = token.toLowerCase()

      if (normalized.startsWith('#x')) {
        const codePoint = Number.parseInt(normalized.slice(2), 16)
        return codePoint >= 0 && codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : entity
      }

      if (normalized.startsWith('#')) {
        const codePoint = Number.parseInt(normalized.slice(1), 10)
        return codePoint >= 0 && codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : entity
      }

      return HTML_ENTITIES[normalized] ?? entity
    })
    .replace(/\s+/g, ' ')
    .trim()
}

export function truncate(value: string, max = 160) {
  if (value.length <= max) return value
  const candidate = value.slice(0, max - 1).trimEnd()
  const lastSpace = candidate.lastIndexOf(' ')
  const safe = lastSpace >= Math.floor(max * 0.65) ? candidate.slice(0, lastSpace) : candidate
  const completePhrase = safe
    .replace(/[,:;\-]+$/g, '')
    .replace(/\s+\b(?:dan|atau|untuk|dengan|dari|ke|di|yang|serta|agar|karena|hingga|sampai)$/i, '')
    .trimEnd()
  return `${completePhrase}…`
}

// Build a `${core} | Dropify` page title, enriching it with the brand USP only
// when it falls under Google's recommended minimum length. Long titles are left
// as-is so they aren't pushed past the SERP truncation limit.
export function brandTitle(
  core: string,
  { usp = 'Distributor Tangan Pertama', min = 30, max = 65 }: { usp?: string; min?: number; max?: number } = {},
) {
  const plain = `${core} | ${SITE_NAME}`
  if (plain.length >= min) return plain
  const enriched = `${core}: ${usp} | ${SITE_NAME}`
  return enriched.length <= max ? enriched : plain
}

// Meta description for a product, falling back to a templated, human-readable
// sentence when the API supplies no meta_description/description. Never emits a
// developer placeholder string into production markup.
// Append a USP/CTA suffix to a meta description that is shorter than the minimum
// so there's room left to communicate benefits and a call to action. The suffix
// should carry a page-unique token (e.g. product/category name) so padding short
// descriptions doesn't collapse them into duplicates.
export function padMetaDescription(text: string, suffix: string, min = 70, max = 155) {
  const base = text.trim()
  if (!base) return truncate(suffix, max)
  if (base.length >= min) return truncate(base, max)
  const separator = /[.!?…]$/.test(base) ? ' ' : '. '
  return truncate(`${base}${separator}${suffix}`, max)
}

export function productMetaDescription(product: StorefrontProduct, max = 155) {
  if (isMaskProduct(product)) {
    return truncate(maskMetaDescription(product), max)
  }

  const raw = stripHtml(product.meta_description || product.description)
  const category = product.categories.map((item) => item.name).filter(Boolean).join(', ')

  if (raw) {
    const suffix = `${product.name} untuk kebutuhan grosir. Hubungi admin untuk cek stok dan quotation.`
    return padMetaDescription(raw, suffix, 70, max)
  }

  const categoryTail = category ? ` Kategori ${category}.` : ''
  return truncate(
    `Grosir ${product.name} dari Dropify.${categoryTail} Hubungi admin untuk cek stok dan quotation.`,
    max,
  )
}

// Meta description for a blog post, with a templated fallback when the post has
// no excerpt/content yet.
export function blogMetaDescription(post: StorefrontBlogPost, max = 155) {
  const raw = stripHtml(post.meta_description || post.excerpt || post.content)
  if (raw) return truncate(raw, max)
  return truncate(`${post.title}: tips dan informasi seputar bisnis grosir dari Dropify.`, max)
}

export function blogMetaTitle(post: StorefrontBlogPost, section = SITE_NAME) {
  const saved = post.meta_title?.trim()
  if (saved) return saved

  const suffix = section === SITE_NAME ? SITE_NAME : `${section} ${SITE_NAME}`
  const title = post.title.trim()
  return new RegExp(`\\|\\s*${suffix}$`, 'i').test(title) ? title : `${title} | ${suffix}`
}

// `link` renders an extra in-answer link in the UI (e.g. to /minimum-order). It
// is deliberately left out of the JSON-LD schema, which only carries q/a text.
export type FaqItem = { q: string; a: string; link?: { href: string; label: string } }

// Category pages have no FAQ field in the API, so we generate a category-tailored
// set from the name. Weaving the category name into every Q&A keeps each page's
// FAQ unique (no duplicate-content penalty) and reinforces the page's keyword.
export function buildCategoryFaq(rawName: string | null | undefined): FaqItem[] {
  const name = (rawName || 'produk').trim()
  return [
    {
      q: `Berapa minimum order (MOQ) untuk grosir ${name}?`,
      a: `Minimum order grosir ${name} mengikuti kategori dan produk. Lihat halaman Minimum Order atau kirim nama barang serta jumlah kebutuhan melalui WhatsApp agar admin dapat memeriksa ketentuannya.`,
    },
    {
      q: `Apakah harga ${name} yang tampil sudah harga grosir?`,
      a: `Harga mengikuti model, jumlah, dan stok saat quotation dibuat. Pilih barang yang diperlukan lalu minta admin memeriksa harga grosir serta satuan pembeliannya.`,
    },
    {
      q: `Apakah bisa pesan ${name} campuran ukuran dan warna?`,
      a: `Ketentuan campur ukuran atau warna mengikuti model dan kemasan. Kirim pilihan yang diperlukan agar admin dapat memeriksanya sebelum quotation dibuat.`,
    },
    {
      q: `Apakah bisa kirim sample ${name} dulu?`,
      a: `Ketersediaan sample, isi, biaya, dan pengirimannya mengikuti produk yang dipilih. Tanyakan melalui WhatsApp sebelum memesan sample.`,
    },
    {
      q: `Berapa lama pengiriman ${name} setelah pembayaran dikonfirmasi?`,
      a: `Kirim kota tujuan dan target penerimaan. Admin akan memeriksa stok, pilihan pengiriman, ongkir, serta estimasi yang tersedia sebelum quotation dibuat.`,
    },
    {
      q: `Informasi apa yang perlu dikonfirmasi sebelum memesan ${name}?`,
      a: `Konfirmasikan model atau SKU, jumlah, ukuran atau warna bila ada, kota tujuan, dan dokumen yang diperlukan. Admin dapat mengirim foto stok aktual serta informasi produk yang tersedia sebelum quotation dibuat.`,
    },
    {
      q: `Bagaimana cara order grosir ${name} di Dropify?`,
      a: `Pilih produk ${name} yang Anda perlukan lalu hubungi kami via WhatsApp dengan jumlah serta kota tujuan. Admin akan mengonfirmasi stok, harga grosir, dan proses pemesanan yang berlaku.`,
    },
  ]
}

// Generic, product-tailored FAQ for product detail pages (how to order, MOQ,
// payment, shipping, etc.). Like the category FAQ, the product name is woven in
// so each page's FAQ stays unique for SEO.
export function buildProductFaq(product: StorefrontProduct): FaqItem[] {
  const name = (product.name || 'produk ini').trim()
  const orderFacts = getProductOrderFacts(product)
  return [
    {
      q: `Bagaimana cara order ${name}?`,
      a: `Klik tombol WhatsApp di halaman ini, lalu kirim nama ${name}, jumlah kebutuhan, dan kota tujuan. Admin akan memeriksa stok, harga, satuan pembelian, serta proses pemesanan yang berlaku.`,
    },
    {
      q: `Berapa minimum order (MOQ) untuk ${name}?`,
      a: orderFacts
        ? `${orderFacts.minimumLabel}${orderFacts.packLabel ? ` ${orderFacts.packLabel}` : ''} Harga akhir mengikuti model dan jumlah saat quotation dibuat.`
        : `Minimum order ${name} berbeda tiap kategori. Lihat halaman Minimum Order atau kirim nama produk dan jumlah kebutuhan melalui WhatsApp agar admin dapat memeriksa ketentuannya.`,
    },
    {
      q: `Apakah ${name} bisa dipesan campuran ukuran dan warna?`,
      a: `Ketentuan campur ukuran atau warna mengikuti model dan kemasan ${name}. Kirim pilihan yang diperlukan agar admin dapat memeriksanya sebelum quotation dibuat.`,
    },
    {
      q: `Apakah bisa kirim sample ${name} dulu?`,
      a: `Ketersediaan sample, isi, biaya, dan pengirimannya mengikuti ${name} yang dipilih. Tanyakan melalui WhatsApp sebelum memesan sample.`,
    },
    {
      q: `Metode pembayaran apa yang tersedia untuk ${name}?`,
      a: `Sampaikan kebutuhan administrasi atau pembayaran saat meminta quotation. Admin akan mengonfirmasi metode yang tersedia untuk pesanan ${name}.`,
    },
    {
      q: `Berapa lama pengiriman ${name} setelah pembayaran?`,
      a: `Kirim kota tujuan dan target penerimaan. Admin akan memeriksa stok, pilihan pengiriman, ongkir, serta estimasi yang tersedia untuk pesanan ${name}.`,
    },
    {
      q: `Informasi apa yang perlu dikonfirmasi sebelum memesan ${name}?`,
      a: `Konfirmasikan model atau SKU, jumlah, ukuran atau warna bila ada, kota tujuan, dan dokumen yang diperlukan. Admin dapat mengirim foto stok aktual serta informasi produk yang tersedia sebelum quotation dibuat.`,
    },
  ]
}

export function buildFaqSchema(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }
}

export function buildBreadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

export function buildBrandLandingSchema({
  brandName,
  pageName,
  description,
  path,
}: {
  brandName: string
  pageName: string
  description: string
  path: string
}) {
  const url = absoluteUrl(path)

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${url}#webpage`,
    url,
    name: pageName,
    description,
    inLanguage: 'id-ID',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: SITE_URL,
    },
    about: {
      '@type': 'Brand',
      '@id': `${url}#brand`,
      name: brandName,
    },
    publisher: {
      '@type': 'WholesaleStore',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
    },
  }
}

export function buildBlogPostingSchema(post: StorefrontBlogPost, path = `/blog/${post.slug || post.id}`) {
  const image = absoluteUrl(toExternalImage(post.featured_image_url) || DEFAULT_OG_IMAGE)

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: blogMetaDescription(post, 300),
    image: [image],
    mainEntityOfPage: absoluteUrl(path),
    datePublished: post.published_at || undefined,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
  }
}

export function categoryOgImage(category: StorefrontCategory | null | undefined) {
  return toExternalImage(category?.image_url) || DEFAULT_OG_IMAGE
}
